import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { papi, type PortalMe, type PortalTicketRow } from './api';
import { fmtDate, statusLabel, usePortalText } from './text';
import { pPrimary } from './PortalApp';

export function StatusPill({ status, holdReason }: { status: string; holdReason: string | null }) {
  const { t } = usePortalText();
  const tone = status === 'pending_approval' || (status === 'on_hold' && holdReason === 'customer') || status === 'resolved'
    ? 'bg-[#FFF1DE] text-[#8A4B00] ring-[#FFD8A3]'
    : ['closed', 'cancelled', 'rejected', 'converted'].includes(status) ? 'bg-[#EEF1F2] text-[#5B6B73] ring-[#DCE3E6]'
      : status === 'approved' ? 'bg-asx-soft text-[#2E5A12] ring-[#CFE6B8]'
        : 'bg-[#E8F0F5] text-asx-navy ring-[#CFDEE8]';
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone}`}>{statusLabel(t, status, holdReason)}</span>;
}

export default function PortalList({ me }: { me: PortalMe }) {
  const { slug = '' } = useParams();
  const { t, lang } = usePortalText();
  const [params, setParams] = useSearchParams();
  const scope = me.role === 'approver' && params.get('scope') === 'company' ? 'company' : 'mine';
  const q = useQuery({ queryKey: ['portal-tickets', scope], queryFn: () => papi<{ tickets: PortalTicketRow[] }>(`/tickets?scope=${scope}`), refetchInterval: 60_000 });
  const tickets = q.data?.tickets ?? [];
  const awaiting = tickets.filter(x => x.awaitingApproval);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{scope === 'company' ? t.companyTickets : t.myTickets}</h1>
          <p className="mt-1 text-sm text-[#5B6B73]">{me.organizationName}</p>
        </div>
        <Link to={`/t/${slug}/nuevo`} className={pPrimary}>+ {t.newTicket}</Link>
      </div>

      {me.role === 'approver' && (
        <div role="tablist" className="flex gap-1 border-b border-[#E1E7E9]">
          {(['mine', 'company'] as const).map(s => (
            <button key={s} type="button" role="tab" aria-selected={scope === s}
              onClick={() => setParams(s === 'company' ? { scope: s } : {}, { replace: true })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${scope === s ? 'border-asx-green font-semibold' : 'border-transparent text-[#5B6B73] hover:text-asx-navy'}`}>
              {s === 'mine' ? t.myTickets : t.companyTickets}
            </button>
          ))}
          <Link to={`/t/${slug}/estados`} className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-[#5B6B73] hover:text-asx-navy">{t.statements}</Link>
        </div>
      )}

      {awaiting.length > 0 && (
        <section className="rounded-xl border border-[#FFD8A3] bg-[#FFF8EE] p-4">
          <h2 className="text-sm font-semibold text-[#8A4B00]">{t.awaitingYou}</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {awaiting.map(x => <li key={x.number}><Link className="font-medium underline-offset-2 hover:underline" to={`/t/${slug}/tickets/${x.number}`}>{x.number} · {x.subject}</Link></li>)}
          </ul>
        </section>
      )}

      {q.isLoading ? <p role="status" className="py-10 text-center text-sm text-[#5B6B73]">{t.loading}</p>
        : tickets.length === 0 ? (
          <section className="rounded-xl border border-[#E1E7E9] bg-white px-6 py-14 text-center">
            <p className="text-lg font-semibold">{t.empty}</p>
            <p className="mt-1 text-sm text-[#5B6B73]">{t.emptyBody}</p>
          </section>
        ) : (
          <ul className="flex flex-col gap-2">
            {tickets.map(x => (
              <li key={x.number}>
                <Link to={`/t/${slug}/tickets/${x.number}`}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3.5 hover:border-[#B9C9D1] ${x.open ? 'border-[#E1E7E9]' : 'border-[#EEF1F2] opacity-80'}`}>
                  <div className="min-w-0 flex-[1_1_320px]">
                    <p className="font-mono text-xs text-[#5B6B73]">{x.number} · {t.types[x.type]}</p>
                    <p className="mt-0.5 truncate font-medium">{x.subject}</p>
                    {scope === 'company' && <p className="mt-0.5 text-xs text-[#5B6B73]">{t.requester}: {x.requesterName}</p>}
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <span className="hidden text-xs text-[#5B6B73] sm:block">{t.updated} {fmtDate(lang, x.updatedAt, true)}</span>
                    <StatusPill status={x.status} holdReason={x.holdReason} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
